import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NotificationBanner from '../../components/ui/NotificationBanner';
import DiscoverSection from '../../components/ui/DiscoverSection';
import schoolSolution from '../../assets/images/solutions/experience_video_cover.jpg';
import corporateSolution from '../../assets/images/solutions/corporate_solution.jpg';
import homeSolution from '../../assets/images/solutions/home_solution.jpg';
import partnershipImage from '../../assets/images/solutions/experience_video_cover.jpg';

const Solutions = () => {
    return (
        <>
            <Helmet>
                <title>Solutions - Nexgen Learning - AI-Driven Education</title>
                <meta name="description" content="Explore NexGen Learning's comprehensive educational solutions for students, educators, and institutions." />
            </Helmet>
            <Header />
            <NotificationBanner />
            <main>
                {/* Hero Section */}
                <section className="pt-28 pb-20 bg-gradient-to-b from-white to-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h1 className="text-5xl py-10 text-primary">|</h1>
                            <h1 className="text-4xl font-bold text-gray-900 mb-6">
                                Solutions
                            </h1>
                            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                                At NexGen Learning, we provide tailored solutions designed to meet the unique needs of various educational environments. Explore how our AI-driven tools can enhance learning experiences for schools, corporate training, and home education.
                            </p>
                        </div>
                    </div>
                </section>

                {/* School Solutions Section */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            {/* Left Side - Image */}
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-2xl transform rotate-3"></div>
                                <img
                                    src={schoolSolution}
                                    alt="School Solutions"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>

                            {/* Right Side - Description */}
                            <div className="space-y-6">
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    NexGen Learning for Schools
                                </h2>
                                <h3 className="text-xl text-primary font-semibold">
                                    Enhance Classroom Engagement and Improve Outcomes
                                </h3>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    NexGen Learning empowers educators with AI tools that foster student engagement and personalize learning experiences. Our solutions help teachers tailor instruction to meet the unique needs of each student, creating a more dynamic and interactive classroom environment. From real-time analytics to adaptive learning paths, we equip schools with the resources they need to inspire and educate effectively.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Curriculum Integration</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Progress Monitoring</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Teacher Support</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Corporate Solutions Section */}
                <section className="py-20 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            {/* Left Side - Description */}
                            <div className="space-y-6">
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    NexGen Learning for Corporate Training
                                </h2>
                                <h3 className="text-xl text-primary font-semibold">
                                    Equip Your Employees with the Skills They Need
                                </h3>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    In today's fast-paced business environment, continuous learning is essential. NexGen Learning provides personalized training solutions that enable organizations to enhance employee skills and drive productivity. Our AI-powered modules adapt to each learner's pace and style, ensuring your team receives training tailored to their needs. Invest in your workforce with our innovative learning solutions that lead to measurable results.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Personalized Training</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Adaptive Learning</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Measurable Results</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side - Image */}
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-2xl transform -rotate-3"></div>
                                <img
                                    src={corporateSolution}
                                    alt="Corporate Solutions"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Home Education Section */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            {/* Left Side - Image */}
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-2xl transform rotate-3"></div>
                                <img
                                    src={homeSolution}
                                    alt="Home Education Solutions"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>

                            {/* Right Side - Description */}
                            <div className="space-y-6">
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    NexGen Learning for Parents & Tutors
                                </h2>
                                <h3 className="text-xl text-primary font-semibold">
                                    Make Learning Engaging and Effective
                                </h3>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    NexGen Learning brings the power of AI into the home, helping parents and tutors create engaging, personalized learning experiences for children. Our intuitive platform provides guidance and resources that adapt to each learner's strengths and areas for improvement. With our tools, you can make learning not just effective, but enjoyable for your child.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Personalized Learning</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Adaptive Resources</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Engaging Experience</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                                Join our global community and start learning today!
                            </h2>
                            <a
                                href="/"
                                className="inline-flex items-center px-8 py-4 border border-transparent text-base font-medium rounded-full text-white bg-primary hover:bg-primary-dark transition-colors duration-200 shadow-lg hover:shadow-xl"
                            >
                                Start your free trial
                                <svg
                                    className="ml-2 -mr-1 w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                                    />
                                </svg>
                            </a>
                        </div>
                    </div>
                </section>

                {/* Partnerships Section */}
                <section className="py-20 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            {/* Left Side - Description */}
                            <div className="space-y-6">
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    Educational Partnerships
                                </h2>
                                <h3 className="text-xl text-primary font-semibold">
                                    Collaborating for Better Learning
                                </h3>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    NexGen Learning partners with leading educational institutions and innovative EdTech companies to deliver personalized, AI-driven learning experiences worldwide. We offer flexible solutions, including interactive courses and adaptive study tools, making education more accessible and engaging for every learner.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Global Reach</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Flexible Solutions</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Innovative Tools</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side - Image */}
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-2xl transform -rotate-3"></div>
                                <img
                                    src={partnershipImage}
                                    alt="Educational Partnerships"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <DiscoverSection />
            <Footer />
        </>
    );
};

export default Solutions; 